import { Link } from 'react-router-dom';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';

export type StepRailStatus = 'done' | 'current' | 'needs_attention' | 'todo';

export interface StepRailItem {
  key: string;
  label: string;
  status: StepRailStatus;
  /** One line shown under the label. */
  summary?: string;
  /** The summary's data is still loading. */
  loading?: boolean;
  to: string;
}

interface StepRailProps {
  steps: readonly StepRailItem[];
  /** The step on screen: gets aria-current="step". */
  currentKey: string;
  ariaLabel: string;
  /** Called after a step link is chosen (e.g. to close a sheet). */
  onNavigate?: () => void;
  'data-testid'?: string;
}

const STATUS_TEXT: Record<StepRailStatus, string> = {
  done: 'Done',
  current: 'Current step',
  needs_attention: 'Needs attention',
  todo: 'Not started',
};

function StepMarker({ status, index }: { status: StepRailStatus; index: number }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'z-10 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 text-xs font-semibold',
        status === 'done' && 'border-success bg-success text-success-foreground',
        status === 'current' && 'border-primary bg-primary text-primary-foreground',
        status === 'needs_attention' && 'border-warning bg-background text-warning',
        status === 'todo' && 'border-input bg-background text-muted-foreground'
      )}
    >
      {status === 'done' ? <Check className="h-4 w-4" /> : index + 1}
    </span>
  );
}

/** Ordered list of steps with a state marker, a one-line summary and aria-current on the step on screen. */
export function StepRail({ steps, currentKey, ariaLabel, onNavigate, 'data-testid': testId }: StepRailProps) {
  return (
    <nav aria-label={ariaLabel} data-testid={testId}>
      <ol className="flex flex-col">
        {steps.map((step, index) => {
          const isCurrent = step.key === currentKey;
          return (
            <li key={step.key} className="relative" data-testid={`${testId ?? 'step-rail'}-item-${step.key}`} data-status={step.status}>
              {index < steps.length - 1 && (
                <span aria-hidden="true" className="absolute left-[13px] top-9 bottom-0 w-px bg-border" />
              )}
              <Link
                to={step.to}
                onClick={onNavigate}
                aria-current={isCurrent ? 'step' : undefined}
                data-testid={`${testId ?? 'step-rail'}-link-${step.key}`}
                className="flex min-h-[44px] items-start gap-3 rounded-md py-2 pr-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <StepMarker status={step.status} index={index} />
                <span className="min-w-0">
                  <span
                    className={cn(
                      'block text-sm',
                      isCurrent ? 'font-semibold text-foreground' : 'font-medium text-foreground'
                    )}
                  >
                    {step.label}
                    <span className="sr-only">{`. ${STATUS_TEXT[step.status]}`}</span>
                  </span>
                  {step.loading ? (
                    <Skeleton aria-hidden="true" className="mt-1 h-3 w-32" />
                  ) : (
                    step.summary && <span className="block text-xs text-muted-foreground break-words">{step.summary}</span>
                  )}
                </span>
              </Link>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
